import * as React from 'react';
import { SectionSpec } from './common/types';

export function useSectionGroups(specs: SectionSpec[]) {
  const [sectionOpen, setSectionOpen] = React.useState<Record<string, boolean>>(
    () => Object.fromEntries(specs.map(s => [s.key, false]))
  );

  const [innerCollapsibleOpen, setInnerCollapsibleOpen] = React.useState<
    Record<string, Record<string, boolean>>
  >(() =>
    Object.fromEntries(
      specs.map(s => [
        s.key,
        Object.fromEntries(s.innerCollapsible.map(k => [k, false])),
      ])
    )
  );

  const isSectionOpen = React.useCallback(
    (key: string) => sectionOpen[key],
    [sectionOpen]
  );

  const toggleSection = React.useCallback((key: string) => {
    setSectionOpen(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const setSection = React.useCallback((key: string, open: boolean) => {
    setSectionOpen(prev => ({ ...prev, [key]: open }));
  }, []);

  const isInnerCollapsibleOpen = React.useCallback(
    (section: string, inner: string) =>
      !!innerCollapsibleOpen[section]?.[inner],
    [innerCollapsibleOpen]
  );

  const toggleInner = React.useCallback((section: string, inner: string) => {
    setInnerCollapsibleOpen(prev => {
      const prevSection = prev[section];

      const nextSection = prevSection
        ? {
            ...prevSection,
            [inner]: !prevSection[inner],
          }
        : {
            [inner]: true,
          };

      return {
        ...prev,
        [section]: nextSection,
      };
    });
  }, []);

  const setInner = React.useCallback(
    (section: string, inner: string, open: boolean) => {
      setInnerCollapsibleOpen(prev => {
        const prevSection = prev[section];

        const nextSection = prevSection
          ? { ...prevSection, [inner]: open }
          : { [inner]: open };

        return {
          ...prev,
          [section]: nextSection,
        };
      });
    },
    []
  );

  const setAllSections = React.useCallback((open: boolean) => {
    setSectionOpen(prev =>
      Object.fromEntries(Object.keys(prev).map(k => [k, open]))
    );
  }, []);

  const setAllInnersInSection = React.useCallback(
    (section: string, open: boolean) => {
      setInnerCollapsibleOpen(prev => {
        const current = prev[section] ?? {};
        const next = Object.fromEntries(
          Object.keys(current).map(k => [k, open])
        );
        return {
          ...prev,
          [section]: next,
        };
      });
    },
    []
  );

  return {
    isSectionOpen,
    toggleSection,
    setSection,
    setAllSections,
    isInnerCollapsibleOpen,
    toggleInner,
    setInner,
    setAllInnersInSection,
  } as const;
}
